/**
 * SHPFLT Header Component
 * Custom element for Shopigurus Header section
 */
class ShpfltHeader extends HTMLElement {
  constructor() {
    super();

    // Selectors
    this.selectors = {
      drawer: '.shpflt-drawer',
      trigger: '.shpflt-mobile-trigger',
      close: '.shpflt-drawer__close',
      overlay: '.shpflt-drawer__overlay',
      navLinks: '.shpflt-drawer-nav__link[data-target]',
      backButtons: '.shpflt-drawer__back',
      subLevels: '.shpflt-drawer__level--sub',
    };

    // Elements
    this.drawer = this.querySelector(this.selectors.drawer);
    this.trigger = this.querySelector(this.selectors.trigger);
    this.closeBtn = this.querySelector(this.selectors.close);
    this.overlay = this.querySelector(this.selectors.overlay);

    // Sticky state tracking - use higher threshold to account for announcement bar
    this.stickyThreshold = 100;
    this.stickyThresholdExit = 80; // Hysteresis: lower threshold to exit sticky state
    this.scrollDirectionDeadzone = 2; // Minimum scroll delta to change direction
    this.lastScrollY = 0;
    this.scrollDirection = 'none';
    this.isInitialized = false;
    this.isStickyActive = false; // Track sticky state to prevent unnecessary updates

    // Bind methods
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.handleDrillDown = this.handleDrillDown.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.trapFocus = this.trapFocus.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  init() {
    // Drawer events
    if (this.trigger) {
      this.trigger.addEventListener('click', this.openDrawer);
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.closeDrawer);
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', this.closeDrawer);
    }

    // Drill-down navigation
    this.querySelectorAll(this.selectors.navLinks).forEach((link) => {
      link.addEventListener('click', this.handleDrillDown);
    });

    this.querySelectorAll(this.selectors.backButtons).forEach((btn) => {
      btn.addEventListener('click', this.handleBack);
    });

    // Initialize sticky/transparent header tracking
    this.initStickyTracking();

    // Shopify Editor events
    this.initEditorEvents();
  }

  // ==========================================================================
  // STICKY HEADER TRACKING
  // ==========================================================================

  initStickyTracking() {
    const sticky = this.getAttribute('sticky');
    const transparent = this.getAttribute('transparent');

    // Only track scroll if header has sticky or transparent behavior
    if (sticky || transparent) {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      // Small delay before initial check to let page settle at correct scroll position
      requestAnimationFrame(() => {
        this.isInitialized = true;
        this.handleScroll();
      });
    }
  }

  handleScroll() {
    // Don't update sticky state if body scroll is locked (drawer/modal is open)
    // Check for various methods of locking body scroll
    const isBodyLocked = document.body.style.overflow === 'hidden' || 
                        document.body.style.position === 'fixed' ||
                        document.body.classList.contains('no-scroll') ||
                        document.documentElement.style.overflow === 'hidden' ||
                        document.querySelector('dialog[open]') !== null;
    
    if (isBodyLocked) {
      return;
    }

    const currentScrollY = window.scrollY;
    const sticky = this.getAttribute('sticky');
    const scrollDelta = Math.abs(currentScrollY - this.lastScrollY);

    // Determine scroll direction only if there's meaningful movement (deadzone)
    if (scrollDelta >= this.scrollDirectionDeadzone) {
      if (currentScrollY > this.lastScrollY) {
        this.scrollDirection = 'down';
      } else if (currentScrollY < this.lastScrollY) {
        this.scrollDirection = 'up';
      }
    }
    // If scrollDelta is less than deadzone, keep previous direction

    // Use hysteresis: different thresholds for entering vs exiting sticky state
    const shouldBeSticky = this.isStickyActive 
      ? currentScrollY > this.stickyThresholdExit  // Use lower threshold to exit
      : currentScrollY > this.stickyThreshold;      // Use higher threshold to enter

    // Update sticky state based on scroll position with hysteresis
    if (shouldBeSticky) {
      // We've scrolled past threshold - header is "sticky active"
      if (!this.isStickyActive) {
        this.isStickyActive = true;
        this.dataset.stickyState = 'active';
      }
      
      // Only update scroll direction if it changed or if we just became sticky
      const currentDirection = this.scrollDirection || 'none';
      if (this.dataset.scrollDirection !== currentDirection) {
        this.dataset.scrollDirection = currentDirection;
      }

      // Handle scroll-up sticky behavior
      if (sticky === 'scroll-up') {
        if (this.scrollDirection === 'down') {
          this.style.transform = 'translateY(-100%)';
        } else {
          this.style.transform = 'translateY(0)';
        }
      }
    } else {
      // At top of page - header is in default state
      if (this.isStickyActive) {
        this.isStickyActive = false;
        this.removeAttribute('data-sticky-state');
      }
      
      // Reset scroll direction when at top
      if (this.dataset.scrollDirection !== 'none') {
        this.dataset.scrollDirection = 'none';
      }
      
      if (sticky === 'scroll-up') {
        this.style.transform = '';
      }
    }

    this.lastScrollY = currentScrollY;
  }

  destroy() {
    if (this.trigger) {
      this.trigger.removeEventListener('click', this.openDrawer);
    }
    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this.closeDrawer);
    }
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.closeDrawer);
    }
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('scroll', this.handleScroll);
  }

  openDrawer() {
    if (!this.drawer) return;

    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.handleKeydown);

    // Focus management
    requestAnimationFrame(() => {
      const closeBtn = this.drawer.querySelector('.shpflt-drawer__close');
      if (closeBtn) closeBtn.focus();
    });

    // Dispatch event
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  closeDrawer() {
    if (!this.drawer) return;

    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.handleKeydown);

    // Reset drill-down after transition
    setTimeout(() => {
      this.resetDrillDown();
    }, 300);

    // Return focus to trigger
    if (this.trigger) this.trigger.focus();

    // Dispatch event
    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }

  handleDrillDown(event) {
  const targetId = event.currentTarget.dataset.target;
  const targetPanel = this.querySelector(`#${CSS.escape(targetId)}`);
  if (!targetPanel || !this.drawer) return;

  // Close others (even if you believe it already resets)
  this.querySelectorAll(this.selectors.subLevels).forEach((p) =>
    p.classList.remove('shpflt-drawer__level--active')
  );

  // Ensure main is in drill mode first
  this.drawer.classList.add('drill-down-active');

  // IMPORTANT: force layout so initial transform state is committed
  // This is the “toggling transform” you were doing manually.
  targetPanel.getBoundingClientRect();

  // Next frame, activate so transition runs reliably
  requestAnimationFrame(() => {
    targetPanel.classList.add('shpflt-drawer__level--active');

    requestAnimationFrame(() => {
      const backBtn = targetPanel.querySelector('.shpflt-drawer__back');
      if (backBtn) backBtn.focus();
    });
  });
  }

  handleBack(event) {
    const currentPanel = event.currentTarget.closest('.shpflt-drawer__level--sub');
    if (currentPanel) {
      currentPanel.classList.remove('shpflt-drawer__level--active');
      this.drawer.classList.remove('drill-down-active');
    }
  }

  resetDrillDown() {
    if (!this.drawer) return;

    this.drawer.classList.remove('drill-down-active');
    this.querySelectorAll(this.selectors.subLevels).forEach((panel) => {
      panel.classList.remove('shpflt-drawer__level--active');
    });
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeDrawer();
    }

    // Focus trap
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  trapFocus(event) {
    if (!this.drawer) return;

    const focusableElements = this.drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  // Shopify Theme Editor Integration
  initEditorEvents() {
    document.addEventListener('shopify:section:load', this.handleSectionLoad.bind(this));
    document.addEventListener('shopify:section:unload', this.handleSectionUnload.bind(this));
    document.addEventListener('shopify:section:select', this.handleSectionSelect.bind(this));
    document.addEventListener('shopify:section:deselect', this.handleSectionDeselect.bind(this));
    document.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this));
    document.addEventListener('shopify:block:deselect', this.handleBlockDeselect.bind(this));
  }

  handleSectionLoad(event) {
    if (!event.target.contains(this)) return;
    this.init();
  }

  handleSectionUnload(event) {
    if (!event.target.contains(this)) return;
    this.destroy();
  }

  handleSectionSelect(event) {
    if (!event.target.contains(this)) return;
    // Keep header visible when selected
  }

  handleSectionDeselect(event) {
    if (!event.target.contains(this)) return;
    this.closeDrawer();
  }

  handleBlockSelect(event) {
    if (!event.target.contains(this)) return;
    // Could open specific menu when block is selected
  }

  handleBlockDeselect(event) {
    if (!event.target.contains(this)) return;
  }
}

// Register Custom Element
if (!customElements.get('shpflt-header')) {
  customElements.define('shpflt-header', ShpfltHeader);
}
